import {
  Divider,
  GKEditor,
  GKTextField,
  Grid2,
  InfoItalicIcon,
  Typography,
} from "components";
import GKImageUploader from "components/GKImageUploader";
import { isEmpty } from "lodash";
import { Controller, useFormContext } from "react-hook-form";
import { useUpdateMyWebsiteDetailsMutation } from "store/features/MyWebsite";
import {
  setCroppedYoutubeThumbnail,
  setFocusedField,
  setSelectedYoutubeThumbnail,
} from "store/slices/MyWebsiteSlice";
import { useAppDispatch, useAppSelector } from "store/store";
import { validateNotWhitespaceOnly } from "utils/validationSchema";
import { WebsiteDetails } from "./types";

const AboutMeForm = () => {
  const form = useFormContext<WebsiteDetails>();

  const id = useAppSelector((state) => state?.user?.userData?.companyId);
  const { croppedYoutubeThumbnail, selectedYoutubeThumbnail } = useAppSelector(
    (state) => state?.myWebsite,
  );

  const [updateMyWebsiteDetails] = useUpdateMyWebsiteDetailsMutation();

  const dispatch = useAppDispatch();

  const handleFocus = (name: string) => dispatch(setFocusedField(name));

  const handleSetSelectedImage = (img: string | null | undefined) => {
    dispatch(setSelectedYoutubeThumbnail(img));
  };

  const handleSetCroppedImage = (img: string | null | undefined) => {
    dispatch(setCroppedYoutubeThumbnail(img));
  };

  const handleDeleteImage = async () => {
    if (isEmpty(form.getValues("websiteSection.aboutMe.youtubeThumbnail"))) {
      dispatch(setSelectedYoutubeThumbnail(null));
      dispatch(setCroppedYoutubeThumbnail(null));
      return;
    }

    try {
      await updateMyWebsiteDetails({
        id,
        body: new FormData(),
        path: "about_me/",
        isDelete: true,
        field: "youtubeThumbnail",
      }).unwrap();
    } catch (err) {
      console.error("Update my website details failed:", err);
    }
  };

  return (
    <Grid2 container spacing={3}>
      <Grid2 size={12}>
        <Controller
          name="websiteSection.aboutMe.youtubeLink"
          control={form.control}
          render={({ field }) => (
            <GKTextField
              {...field}
              inputLabel="Youtube Link"
              onFocus={() => handleFocus(field.name)}
              onBlur={() => handleFocus(null)}
            />
          )}
        />
      </Grid2>
      <Grid2 size={12} container spacing={1}>
        <Grid2 size={12}>
          <Typography fontSize={13} fontWeight={500} color="var(--grey-900)">
            Upload Youtube Thumbnail
          </Typography>
        </Grid2>
        <Grid2 size={12}>
          <Controller
            name="websiteSection.aboutMe.youtubeThumbnail"
            control={form.control}
            render={() => (
              <GKImageUploader
                croppedImage={croppedYoutubeThumbnail}
                selectedImage={selectedYoutubeThumbnail}
                setCroppedImage={handleSetCroppedImage}
                setSelectedImage={handleSetSelectedImage}
                form={form}
                logoName={"websiteSection.aboutMe.youtubeThumbnail"}
                buttonName="Upload Youtube Thumbnail"
                handleProfilePicDelete={handleDeleteImage}
              />
            )}
          />
        </Grid2>
      </Grid2>
      <Grid2
        size={12}
        container
        p={"12px 32px 12px 12px"}
        borderRadius={2}
        bgcolor={"var(--yellow-100)"}
        display={"flex"}
        spacing={0}
      >
        <Grid2 size={1}>
          <InfoItalicIcon
            fill="var(--grey-900)"
            style={{ minHeight: 16, minWidth: 16 }}
          />
        </Grid2>
        <Grid2 size={11}>
          <Typography fontSize={12} fontWeight={500}>
            If you don&apos;t have a YouTube video, you can upload any of your
            images to use as a YouTube thumbnail.
          </Typography>
        </Grid2>
      </Grid2>

      <Grid2 size={12}>
        <Divider />
      </Grid2>
      <Grid2 size={12}>
        <Controller
          name="websiteSection.aboutMe.advisorInfo"
          control={form.control}
          rules={{
            required: {
              value: true,
              message: "Please enter description",
            },
            validate: validateNotWhitespaceOnly,
          }}
          render={({ field, fieldState: { error } }) => (
            <GKEditor
              {...field}
              requiredField
              inputLabel="About Yourself"
              error={!!error}
              helperText={error?.message}
              toolbar={[
                "undo",
                "redo",
                "|",
                // "heading",
                "bold",
                "italic",
                "underline",
                "strikethrough",
                "|",
                "fontColor",
                "fontBackgroundColor",
                "|",
                "alignment",
                "link",
                // "|",
                // "bulletedList",
                // "numberedList",
              ]}
              onFocus={() => handleFocus(field.name)}
              onBlur={() => handleFocus(null)}
            />
          )}
        />
      </Grid2>
      <Grid2 size={12}>
        <Controller
          name="websiteSection.aboutMe.yearsOfExperience"
          control={form.control}
          rules={{
            required: {
              value: true,
              message: "Please enter years of experience",
            },
            validate: validateNotWhitespaceOnly,
          }}
          render={({ field, fieldState: { error } }) => (
            <GKTextField
              {...field}
              requiredField
              inputLabel="Years of Experience"
              error={!!error}
              helperText={error?.message}
              onFocus={() => handleFocus(field.name)}
              onBlur={() => handleFocus(null)}
            />
          )}
        />
      </Grid2>
      <Grid2 size={12}>
        <Controller
          name="websiteSection.aboutMe.sebiRegistered"
          control={form.control}
          rules={{
            required: {
              value: true,
              message: "Please enter SEBI Registered number",
            },
            validate: validateNotWhitespaceOnly,
          }}
          render={({ field, fieldState: { error } }) => (
            <GKTextField
              {...field}
              requiredField
              inputLabel="SEBI Registered No."
              error={!!error}
              helperText={error?.message}
              onFocus={() => handleFocus(field.name)}
              onBlur={() => handleFocus(null)}
            />
          )}
        />
      </Grid2>
    </Grid2>
  );
};

export default AboutMeForm;
